import { useCallback, useMemo } from 'react'

import { F0Select, FiltersDefinition, FiltersState } from '@factorialco/f0-react'
import { ResolvedRecordType, SelectItemProps } from '@factorialco/f0-react/dist/experimental'
import { Search } from '@factorialco/f0-react/icons/app'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { EntitySelectorV2Props } from './types'

export const EntitySelectorV2 = <
  ListResult,
  SingleItemResult,
  R,
  ListVariables extends { [key: string]: unknown },
  ForcedFiltersType extends { [key: string]: unknown },
>({
  document: graphqlDocument,
  itemsDocument,
  extractConnection,
  extractItems,
  mapItemTitle,
  mapItemAvatar,
  mapItemValue,
  label,
  hint,
  status,
  messages,
  options,
  disabled,
  filters,
  forcedFilters,
  error,
  mapFiltersToVariables,
  perPage = 25,
  value,
  mandatory,
  children,
  forcedExcludedIds,
  itemsVariables,
  ...props
}: EntitySelectorV2Props<ListResult, SingleItemResult, R, ListVariables, ForcedFiltersType>) => {
  const idsToFetch = useMemo(() => {
    if (value === undefined || value === null) {
      return []
    }
    return Array.isArray(value) ? value : [value]
  }, [value])

  const { data: itemsData } = useQuery(itemsDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      ...itemsVariables,
      ids: idsToFetch,
    },
    skip: idsToFetch.length === 0,
  })

  const fetchedItems = useMemo(() => {
    if (idsToFetch.length === 0 || !itemsData) {
      return []
    }

    return extractItems(itemsData).filter(
      (item): item is NonNullable<typeof item> => item !== null && item !== undefined
    )
  }, [itemsData, extractItems, idsToFetch])

  const getItemValue = useCallback(
    (item: ResolvedRecordType<R>) => (mapItemValue ? mapItemValue(item) : item.id),
    [mapItemValue]
  )

  const source = useGraphqlDataSource(
    {
      paginationType: 'infinite-scroll',
      fetchPolicy: 'cache-first',
      perPage,
      document: graphqlDocument,
      filters,
      mapData: (data) => extractConnection(data).edges.map((edge) => edge.node),
      mapPagination: (data) => {
        const connection = extractConnection(data)
        const pageInfo = connection.pageInfo
        const hasNextPage = !!pageInfo.hasNextPage

        const total = connection.totalCount ?? 0

        return {
          total,
          cursor: pageInfo.endCursor ?? '0',
          hasNextPage,
        }
      },
      selectable: (item: ResolvedRecordType<R>) => String(getItemValue(item)),
      mapVariables: ({
        search,
        filters: filtersState,
      }): Omit<ListVariables, 'cursor' | 'perPage' | 'page'> => {
        const variables = mapFiltersToVariables({ forcedFilters, filters: filtersState })

        return {
          ...variables,
          search,
        }
      },
    },
    [filters, perPage]
  )

  const mapOptions = useCallback(
    (
      item: ResolvedRecordType<R>
    ): SelectItemProps<string, ResolvedRecordType<R>> & { type: 'item' } => ({
      value: String(getItemValue(item)),
      label: mapItemTitle(item),
      avatar: mapItemAvatar?.(item),
      type: 'item',
    }),
    [mapItemTitle, mapItemAvatar, getItemValue]
  )

  const handleChange = useCallback(
    (val: string | undefined) => {
      if (props.multiple === true) {
        return
      }

      if (typeof val !== 'string' || !val) {
        props.onChange(undefined)
        return
      }

      props.onChange(val)
    },
    [props]
  )

  const finalValue = useMemo(() => {
    if (props.multiple === true) {
      return value ? (Array.isArray(value) ? value : [value]).map(String) : undefined
    }

    return value ? String(value) : undefined
  }, [value, props.multiple])

  const defaultItem = useMemo(() => {
    const firstItem = fetchedItems[0]

    if (!firstItem) {
      return undefined
    }

    if (props.multiple === true) {
      return fetchedItems.map(mapOptions)
    }

    return mapOptions(firstItem)
  }, [fetchedItems, mapOptions, props.multiple])

  const existingModals = document.querySelectorAll<HTMLElement>('div[aria-modal="true"]')
  const existingModal =
    existingModals.length > 0 ? existingModals[existingModals.length - 1] : undefined

  const handleSelectItems = useCallback(
    (items: {
      allSelected: boolean | 'indeterminate'
      selectedIds: string[]
      itemsStatus: { item: { id: string }; checked: boolean }[]
      filters: FiltersState<FiltersDefinition> | undefined
      selectedCount: number
    }) => {
      if (props.multiple !== true) {
        return
      }

      const metadata = { selectedCount: items.selectedCount }

      if (items.allSelected === false) {
        props.onChange(
          {
            type: 'manual',
            ids: items.selectedIds,
          },
          metadata
        )
      } else if (items.allSelected === true) {
        props.onChange(
          {
            type: 'all',
            filters: items.filters,
            excludedIds: forcedExcludedIds ?? [],
          },
          metadata
        )
      } else {
        const excludedIds = items.itemsStatus
          .filter((item) => !item.checked)
          .map((item) => item.item.id)

        props.onChange(
          {
            type: 'all',
            filters: items.filters,
            excludedIds: [...(forcedExcludedIds ?? []), ...excludedIds],
          },
          metadata
        )
      }
    },
    [props, forcedExcludedIds]
  )

  return (
    <F0Select
      source={source}
      searchEmptyMessage={
        messages?.searchEmptyMessage ?? i18n.tp('employees.select_employee', { count: 0 })
      }
      defaultItem={defaultItem}
      icon={
        options?.icon || (options?.disableSearch || options?.hideSearchIcon ? undefined : Search)
      }
      label={label}
      hint={hint}
      size={options?.size ?? 'md'}
      placeholder={messages?.placeholder}
      searchBoxPlaceholder={messages?.searchBoxPlaceholder}
      onChange={handleChange}
      value={finalValue}
      disabled={disabled}
      mapOptions={mapOptions}
      error={error}
      hideLabel={options?.hideLabel}
      showSearchBox={!options?.disableSearch}
      clearable={!mandatory}
      portalContainer={existingModal}
      multiple={props.multiple ?? false}
      onSelectItems={handleSelectItems}
      disableSelectAll={options?.disableSelectAll}
      asList={options?.asList}
      status={status}
    >
      {children}
    </F0Select>
  )
}
